import React, { useState } from 'react'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {
  addField,
  availableFields,
  moveDown,
  moveUp,
  removeField,
  type GroupByField,
} from './groupByModel'

export interface GroupByPaneProps {
  fields: GroupByField[]
  draft: string[]
  onDraftChange: (next: string[]) => void
}

export function GroupByPane({ fields, draft, onDraftChange }: GroupByPaneProps) {
  const [selectedAvailable, setSelectedAvailable] = useState<string | null>(null)
  const [selectedDraft, setSelectedDraft] = useState<string | null>(() => draft[0] ?? null)

  const available = availableFields(fields, draft)
  const titleOf = (name: string) => fields.find((f) => f.name === name)?.title || name

  return (
    <Box>
      {fields.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No groupable fields
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gap: 1.5,
          alignItems: 'stretch',
          minHeight: 260,
          pt: 1,
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Available
          </Typography>
          <List
            dense
            role="listbox"
            aria-label="Available"
            tabIndex={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 220 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && selectedAvailable) {
                e.preventDefault()
                onDraftChange(addField(draft, selectedAvailable))
                setSelectedDraft(selectedAvailable)
                setSelectedAvailable(null)
              }
            }}
          >
            {available.map((f) => (
              <ListItemButton
                key={f.name}
                role="option"
                selected={selectedAvailable === f.name}
                onClick={() => setSelectedAvailable(f.name)}
                onDoubleClick={() => {
                  onDraftChange(addField(draft, f.name))
                  setSelectedDraft(f.name)
                  setSelectedAvailable(null)
                }}
              >
                {f.title}
              </ListItemButton>
            ))}
          </List>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, pt: 2 }}>
          <IconButton
            size="small"
            aria-label="Add field"
            onClick={() => {
              if (!selectedAvailable) return
              onDraftChange(addField(draft, selectedAvailable))
              setSelectedDraft(selectedAvailable)
              setSelectedAvailable(null)
            }}
          >
            <Typography aria-hidden>›</Typography>
          </IconButton>
          <IconButton
            size="small"
            aria-label="Remove field"
            onClick={() => {
              if (!selectedDraft) return
              onDraftChange(removeField(draft, selectedDraft))
              setSelectedAvailable(selectedDraft)
              setSelectedDraft(null)
            }}
          >
            <Typography aria-hidden>‹</Typography>
          </IconButton>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Grouping
          </Typography>
          <List
            dense
            role="listbox"
            aria-label="Grouping"
            tabIndex={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, minHeight: 220 }}
            onKeyDown={(e) => {
              if (e.key === 'Delete' && selectedDraft) {
                e.preventDefault()
                onDraftChange(removeField(draft, selectedDraft))
                setSelectedAvailable(selectedDraft)
                setSelectedDraft(null)
              }
            }}
          >
            {draft.map((name, index) => (
              <ListItemButton
                key={name}
                role="option"
                selected={selectedDraft === name}
                onClick={() => setSelectedDraft(name)}
                sx={{ pl: 1 + index * 2 }}
              >
                {titleOf(name)}
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              size="small"
              aria-label="Move up"
              onClick={() => selectedDraft && onDraftChange(moveUp(draft, selectedDraft))}
            >
              Up
            </Button>
            <Button
              size="small"
              aria-label="Move down"
              onClick={() => selectedDraft && onDraftChange(moveDown(draft, selectedDraft))}
            >
              Down
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
